/**
 * Vocalist Web2App - Main Entry Point
 *
 * This is the entry point for the Vocalist web-to-app conversion funnel.
 * It initializes Alpine.js for reactive UI and sets up the application.
 */

// Import styles
import './styles/main.css';

// Import additional CSS for animations and effects
import './styles/congratulations.css';
import './styles/success-fireworks.css';

// Theme-specific CSS is dynamically loaded by app.ts based on A/B test variant
// Available variants: styles.css (purple), styles-blue.css, styles-light.css, styles-dg.css
// The loadStylesheet() method in app.ts handles dynamic loading

// Import Alpine.js
import Alpine from 'alpinejs';

// Import core modules
import { initAlpineStore } from './core/alpine-store';
import app from './core/app';
import userRepository from './data/user-repository';
import { Config } from './core/config';
import earlyAbtestManager from './core/early-abtest-manager';

// Import App type for window declaration
import type { App } from './core/app';

// Make Alpine available globally for debugging and compatibility
declare global {
  interface Window {
    Alpine: typeof Alpine;
    app: App;
    UserRepository: typeof userRepository;
    CONFIG: typeof Config;
    syncAlpineStore?: (key: string, value: unknown) => void;
    syncAlpineEmail?: (email: string) => void;
  }
}

window.Alpine = Alpine;

// Decide the experiment before Alpine or UserRepository initialize. This is deliberately
// synchronous and idempotent so analytics, routing, and the first rendered screen agree.
earlyAbtestManager.resolve();

// Initialize Alpine store
initAlpineStore();

// Initialize Alpine.js
Alpine.start();

/**
 * Initialize the application
 */
export async function initializeApp(): Promise<void> {
  console.log('Vocalist Web2App initialized');
  console.log('Alpine.js version:', Alpine.version);

  try {
    if (earlyAbtestManager.isModern()) {
      try {
        const { installModernExperience } = await import('./v2/runtime');
        installModernExperience();
      } catch (error) {
        // Keep the page usable without attributing Purple UI behavior to the Modern cohort.
        earlyAbtestManager.usePurpleFallback();
        console.error('Modern funnel runtime failed to load; assignment changed to Purple', error);
      }
    }

    // Initialize the app (which uses AppBootstrapper)
    await app.init();

    // Make key services globally available for backward compatibility
    window.app = app;
    window.UserRepository = userRepository;
    window.CONFIG = Config;

    console.log('Application initialized successfully');
  } catch (error) {
    console.error('Failed to initialize application:', error);
    // Error UI is handled by app.init()
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}
